import { Component, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { AccountService } from '@frontendlib/common/services/account.service';
import { BaseSidebarComponent } from '@modules/common/components/base-sidebar/base-sidebar.component';
import { ContainerDndService } from '@modules/common/services/container-dnd.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { StitchService } from '@modules/common/services/stitch.service';
import { DateFormatService } from '@modules/common/services/date-format.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { Application } from '@modules/common/types/application';
import { next7DaysArray } from '@modules/common/types/next-7-days';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { workspaceColor, workspacePalette } from '@modules/common/utils/workspace-color';
import { DragData, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { priorities } from '@modules/form-controls/types/priorities';
import { ProjectsService } from '@modules/tasks/services/projects.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { TasksTenantScopeService } from '@modules/tasks/services/tasks-tenant-scope.service';
import { tasksPinnedMenu } from '@modules/tasks/components/sidebar-context-menu/tasks-rail-menu';
import { Project } from '@modules/tasks/types/project';
import { Task } from '@modules/tasks/types/task';
import { VirtualFolder } from '@modules/tasks/types/virtual-folder';
import { DateByDayIndexPipe } from '@modules/utils/pipes/date-by-day-index.pipe';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { type Space } from '@modules/settings/types/space';
import { Team } from '@modules/settings/types/team';
import { map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-tasks-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.less'],
  standalone: false,
})
export class TasksSideBarComponent
  extends BaseSidebarComponent<Project, Task, VirtualFolder>
  implements OnInit, OnDestroy
{
  private dayByDateIndexPipe = new DateByDayIndexPipe();
  private readonly dateFormatService = inject(DateFormatService);

  public readonly SidebarSplitViewKey = SidebarSplitViewKey;
  protected application = Application.tasking;
  protected sidebarSplitViewKey = SidebarSplitViewKey.tasking;
  protected selfDragDataTypes = [DragDataTypes.task, DragDataTypes.project];

  public baseItems: SidebarItem[] = [
    { title: 'Tasks', id: 'all_tasks', icon: 'sapphire-tasks' },
    { title: 'Overdue', id: 'overdue', icon: 'sapphire-overdue' },
    { title: 'Today', id: 'today', icon: 'sapphire-today' },
    { title: 'Next 7 days', id: 'week', icon: 'sapphire-week' },
    { title: 'Priority', id: 'priority', icon: 'sapphire-priority' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
    ...next7DaysArray.map((day, index) => ({
      title: index === 0 ? 'Tomorrow' : this.dateFormatService.formatDayOfWeek(this.dayByDateIndexPipe.transform(day)),
      id: day,
      parentId: 'week',
    })),
    ...priorities.map(
      (priority): SidebarItem => ({
        title: priority.title,
        id: priority.value,
        parentId: 'priority',
        icon: 'sapphire-priority',
        color: priority.color,
      }),
    ),
  ];

  public readonly primaryItems: SidebarItem[] = [
    { title: 'My Tasks', id: 'all_tasks', icon: 'sapphire-tasks' },
    { title: 'Overdue', id: 'overdue', icon: 'sapphire-overdue' },
    { title: 'Today', id: 'today', icon: 'sapphire-today' },
  ];
  public readonly teamPrimaryItems: SidebarItem[] = this.baseItems.filter(({ id }) =>
    ['all_tasks', 'overdue', 'today'].includes(id),
  );
  public readonly moreItems: SidebarItem[] = this.baseItems.filter(
    ({ id }) => !['all_tasks', 'overdue', 'today'].includes(id),
  );

  /** The Pinned / Team Knots and Tags rows' menus: the prototype's entry, then Tasks' own Add and Manage. */
  protected readonly pinnedMenus = {
    mine: { knots: tasksPinnedMenu('knots', 'mine'), tags: tasksPinnedMenu('tags', 'mine') },
    team: { knots: tasksPinnedMenu('knots', 'team'), tags: tasksPinnedMenu('tags', 'team') },
  } as const;

  protected readonly workspaceColor = workspaceColor;
  protected readonly workspacePalette = workspacePalette;
  protected readonly myWorkspaceExpanded = signal(true);
  protected readonly moreExpanded = signal(false);
  protected readonly teamWorkspaceExpanded = signal(true);
  protected readonly currentTeamWorkspaceExpanded = signal(true);
  protected readonly teamMoreExpanded = signal(false);
  protected readonly workspaceResolved = signal(false);
  protected currentTeamWorkspace: Space | undefined;
  protected currentMemberTenant: Team | undefined;
  protected currentTeamTenant: Team | undefined;
  protected currentTeamWorkspaceInitials = '';

  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly accountService = inject(AccountService);
  private readonly spacesService = inject(SpacesService);
  private readonly tasksTenantScopeService = inject(TasksTenantScopeService);
  private readonly copyDropService = inject(ContainerDndService);
  private readonly copyAlertService = inject(AlertService);
  private readonly copyStitchService = inject(StitchService);

  constructor(
    injector: Injector,
    private tasksService: TasksService,
    private projectsService: ProjectsService,
  ) {
    super(injector);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.accountService
      .getAccount(true)
      .pipe(
        switchMap((account) =>
          this.spacesService
            .listWithInvites({ archived: false, limit: 100 }, { account })
            .pipe(map(({ items }) => items)),
        ),
        takeUntil(this.alive),
      )
      .subscribe({
        next: (items) => this.setCurrentTeamWorkspace(items, this.spaceContextStore.contextId()),
        error: () => this.degradeToPersonal(),
      });
  }

  private degradeToPersonal(): void {
    this.currentTeamWorkspace = undefined;
    this.currentMemberTenant = undefined;
    this.currentTeamTenant = undefined;
    this.currentTeamWorkspaceInitials = '';
    this.tasksTenantScopeService.degradeToPersonal();
    this.workspaceResolved.set(true);
  }

  private setCurrentTeamWorkspace(items: Space[], contextId: string | undefined): void {
    this.currentTeamWorkspace = items.find(
      ({ currentUserStatus, id }) => currentUserStatus === 'accepted' && id === contextId,
    );
    this.currentMemberTenant = this.currentTeamWorkspace?.myMailboxTenantId
      ? new Team({ id: this.currentTeamWorkspace.myMailboxTenantId })
      : undefined;
    this.currentTeamTenant = this.currentTeamWorkspace ? new Team({ id: this.currentTeamWorkspace.id }) : undefined;

    // `holdsLegacyPersonalSelection()` widens the never-visited case to the pre-#961 `null`, which
    // this rail used to send down the else branch — resolving it against the SPACE id and leaving the
    // `null` in place for good (#1065 ROW 1).
    if (
      this.currentMemberTenant &&
      (this.tasksTenantScopeService.tenantId() === undefined ||
        this.tasksTenantScopeService.holdsLegacyPersonalSelection())
    ) {
      this.tasksTenantScopeService.resolveInitialTenant(this.currentMemberTenant.id);
    } else {
      this.tasksTenantScopeService.resolveInitialTenant(this.currentTeamWorkspace?.id ?? null);
    }

    this.currentTeamWorkspaceInitials =
      this.currentTeamWorkspace?.title
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase() ?? '';
    this.workspaceResolved.set(true);
  }

  protected toggleCurrentTeamWorkspace(): void {
    this.currentTeamWorkspaceExpanded.update((expanded) => !expanded);
  }

  /**
   * Actions
   */

  public override dndDrop(
    dragData: DragData, sidebarFolder: VirtualFolder, index?: number, destination: Team | undefined = this.team,
  ): void {
    if (dragData.type === DragDataTypes.task) {
      const destinationOwner = destination?.id ?? this.spaceContextStore.contextId();
      const owners = new Set(dragData.data.map((item) => getStringField(item, 'accountId')));
      if (!dragData.data.length || owners.size !== 1 || !owners.has(destinationOwner)) {
        this.copyDropService.handleChildItemDrop(
          new Project({ accountId: destinationOwner, title: 'Tasks' }), dragData,
          { containerType: DragDataTypes.project, childItemType: DragDataTypes.task,
            childService: this.tasksService, containerIdField: 'projectId', crossTenantCopySupported: true,
            cancelled: this.alive, isActive: () => !this.alive.isStopped, appStateService: this.stateService,
          }, this.copyStitchService, this.copyAlertService,
        );
        return;
      }
      if (sidebarFolder === 'all_tasks' && dragData.data.every((item) =>
        !Reflect.get(item, 'parentId') && !Reflect.get(item, 'projectId') &&
        !Reflect.get(item, 'deleted') && !Reflect.get(item, 'archived'))) return;
    }
    super.dndDrop(dragData, sidebarFolder, index);
  }

  handleMove(dragData: DragData, sidebarFolder: VirtualFolder) {
    const ids = this.getIds(dragData.data);
    const tenantItems = dragData.data.map(item => ({accountId: getStringField(item, 'accountId')}));

    if (sidebarFolder === 'deleted') {
      if (dragData.type === DragDataTypes.task) {
        this.tasksService.delete({ ids }, true, undefined, { tenantItems });
      } else if (dragData.type === DragDataTypes.project) {
        this.projectsService.delete({ ids }, true);
      }
      return;
    }

    // Restore trashed/archived items first — virtual-folder lists filter out archived/deleted
    // items, so applying the changes alone would leave them stuck in Trash/Archive.
    const restoreOptions = { skipArchiveRestore: sidebarFolder === 'archived' };

    if (dragData.type === DragDataTypes.task) {
      const { changes, message } = Task.getChangesForVirtualFolder(sidebarFolder, this.dateFormatService);
      moveItemsIntoContainer(
        this.tasksService,
        dragData.data,
        () => this.tasksService.bunchUpdate({ ids }, changes, { message, tenantItems }),
        restoreOptions,
      );
    } else if (dragData.type === DragDataTypes.project) {
      const { changes, message } = Project.getChangesForVirtualFolder(sidebarFolder, this.dateFormatService);
      moveItemsIntoContainer(
        this.projectsService,
        dragData.data,
        () => this.projectsService.bunchUpdate({ ids }, changes, { message }),
        restoreOptions,
      );
    }
  }

  handleMore(item: Task) {
    this.stateService.setMainView(item);
  }

  getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder) {
    const { changes } = Task.getChangesForVirtualFolder(sidebarFolder, this.dateFormatService);

    return new Task({ ...changes, title: dragData.data[0]['title'] });
  }
}
